import React, { JSX, useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button
} from "@mui/material";
import "../css/CustomerCoverageTable.css";
import { useSalesContext } from "../../Pages/SalesContext";
import { API_BASE_URL2 ,API_BASE_URL} from "../../config/config.js";


interface TableRowData {
  Role: string;
  Name?: string;
  Staff_Name?: string;
  Total_Days_In_Month?: number;
  FW_Days?: number;
  NFA_Meeting_Days?: number;
  Leave?: number;
  Holiday_Incl_Sunday?: number;
  Total_Number_Of_days_Eve_Call_Done?: number;
  children?: TableRowData[];
  [key: string]: any;
}

interface CustomerCoverageTableProps {
  activeCard: string;
  selectedMonth: string;
  userId: string;
  userRole: string;
}
const keyMapping: Record<string, string> = {
  Staff_Name: "Staff Name",
  Total_Days: "Total Days In Month",
  Eve_Call_Days: "Total No. of days Eve call done",
  FW_Days: "FW Days",
  NFA_Meeting_Days: "NFA/Meeting Days",
  Total_Number_Of_days_Eve_Call_Done: "Total No. of days Evening Call Done",
  Total_Days_In_Month: "Total Days In Month",
  Leave: "Leave",
  Holiday_Incl_Sunday: "Holiday Including Sunday",
  Role: "Role",
  A_Calls: "Number of A Calls",
  B_Calls: "Number of B Calls",
  C_Calls: "Number of C Calls",
  DR_Calls: "Total Dr Calls",
  DR_Call_Avg: "Dr Call avg",
  A_Doctor_Count: "Listed A Doctor Count",
  B_Doctor_Count: "Listed B Doctor Count",
  C_Doctor_Count: "Listed C Doctor Count",
  Total_Doctor: "Total Dr. in list",
  Total_Doctor_Count: "Total Doctor Count",
  Total_Doctor_Calls: "Total Doctor Calls",
  FLM_L5M_A_Cvrg_Per: "FLM’s L5M “A Class” Cvrg%",
  FLM_L5M_A_Cvrg: "FLM’s L5M “A Class” Cvrg",
  Unique_A_Dr_Met: "FLM Unique A Dr Met",
  Unique_B_Dr_Met: "FLM Unique B Dr Met",
  Unique_C_Dr_Met: "FLM Unique C Dr Met",
  Unique_Cvrg_Per: "Unique A+B Coverage %",
  Freq_Cvrg_Per: " Frequency A+B Compliance %",
};



const normalizeChildrenKeys = (rows: any[]): TableRowData[] => {
  return rows.map((row) => {
    const { Children, children } = row;

    // Map keys based on your interface
    const mappedRow: TableRowData = {
      Role: ""
    };

    for (const key in row) {
      if (key === "Children" || key === "children") continue;
      const mappedKey = keyMapping[key] || key;
      mappedRow[mappedKey] = row[key];
    }

    // Recursively normalize children
    mappedRow.children = Children
      ? normalizeChildrenKeys(Children)
      : children
      ? normalizeChildrenKeys(children)
      : undefined;

    return mappedRow;
  });
};

const getFallbackData = (): TableRowData[] => [
  {
    Role: "MR",
    Staff_Name: "N/A",
    Total_Days: 0,
    FW_Days: 0,
    NFA_Meeting_Days: 0,
    Leave: 0,
    Holiday_Incl_Sunday: 0,
    Eve_Call_Days: 0,
    children: undefined
  }
];


const CustomerCoverageTable: React.FC<CustomerCoverageTableProps> = React.memo(({
  activeCard,
  selectedMonth,
  userId,
  userRole
}) => {
  const { kpiData  } = useSalesContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [expandedSLM, setExpandedSLM] = useState<string | null>(null);
  const [expandedFLM, setExpandedFLM] = useState<string | null>(null);
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
const [expandedSLMIndexes, setExpandedSLMIndexes] = useState<number[]>([]);
const [expandedFLMPaths, setExpandedFLMPaths] = useState<string[]>([]);



const formatCellValue = (col: string, value: any): string => {
  if (value == null || value === "") return "";

  const colLower = col.toLowerCase();

  // Handle percentage columns
  if (typeof value === "number" && (colLower.includes("per") || col.endsWith("%"))) {
    return `${value.toFixed(2)}%`;
  }
if(typeof value === "number" && colLower.includes("call") && colLower.includes("avg")) {
  return value.toFixed(1);  }
  // Handle days, calls, and months as whole numbers
  if (
    // eslint-disable-next-line no-mixed-operators
    typeof value === "number" &&
    (colLower.includes("day") ||
     colLower.includes("calls") ||
     colLower.includes("month")||
     colLower.includes("leave") ||
      colLower.includes("doctor") ||
      colLower.includes("dr") )
  ) {
    return value.toFixed(0);
  }

  // Fallback for other numbers
  if (typeof value === "number" || typeof value === "string") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }

  return value;
};
const getEffectiveYear = (monthName: string): number => {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const selectedMonthIndex = months.indexOf(monthName); // 0-based
  const now = new Date();
  const currentMonthIndex = now.getMonth();
  const currentYear = now.getFullYear();

  return selectedMonthIndex <= currentMonthIndex
    ? currentYear
    : currentYear - 1;
};



  useEffect(() => {
    if (!activeCard || !selectedMonth) return;

const fetchTableData = async () => {
  setLoading(true);
const effectiveYear = getEffectiveYear(selectedMonth);
  try {
    const response = await fetch(
  `${API_BASE_URL}CustomerCoverage/DashboardKPI`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          userId: userId,
          userRole: userRole,
          month: selectedMonth,
          cardType: activeCard,
          year: effectiveYear.toString(),
        })
      }
    );

    const result = await response.json();
    console.log("API Response:", result);

    // If API response is a direct array (not wrapped in `table_data`)
    const dataToUse = Array.isArray(result)
      ? result
      : result?.table_data?.[activeCard] || [];

    if (dataToUse.length > 0) {
      const normalized = normalizeChildrenKeys(dataToUse);
      setTableData(normalized);
    } else {
      console.warn("Empty or invalid response. Using fallback.");
      setTableData([]);
    }

    setExpandedSLM(null);
    setExpandedFLM(null);
  } catch (error) {
    console.error("Error fetching table data:", error);
    setTableData([]);
  } finally {
    setLoading(false);
  }
};


    fetchTableData();
      setExpandedSLMIndexes([]);
  setExpandedFLMPaths([]);
  }, [activeCard, selectedMonth, userId]);

const handleSLMClick = (index: number) => {
  setExpandedSLMIndexes(prev =>
    prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
  );
};



const handleFLMClick = (slmIndex: number, flmIndex: number) => {
  const path = `${slmIndex}-${flmIndex}`;
  setExpandedFLMPaths(prev =>
    prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
  );
};
const renderRows = (data: TableRowData[]): JSX.Element[] => {
  return data.flatMap((slm, slmIndex) => {
    const slmRowKey = `SLM-${slm.Name}-${slmIndex}`;
    const isSLMSelected = selectedRowKey === slmRowKey;
  const isSLMExpanded = expandedSLMIndexes.includes(slmIndex);

    const slmRow = (
      <TableRow
        key={slmRowKey}
        sx={{
          cursor: "pointer",
          bgcolor: isSLMSelected ? "#454088" : "#ffffff",
          "&:hover": {
            bgcolor: isSLMSelected ? "#454088" : "#e0e0ff"
          },
          color: isSLMSelected ? "#ffffff" : undefined
        }}
        onClick={() => {
          handleSLMClick(slmIndex);
          setSelectedRowKey(slmRowKey);
        }}
      >
        {Object.keys(slm)
          .filter(k => k !== "children")
          .map(key => (
            <TableCell
              key={key}
              sx={{ color: isSLMSelected ? "#ffffff" : undefined, textAlign: "left" }}
            >
              {formatCellValue(key, slm[key])}
            </TableCell>
          ))}
      </TableRow>
    );

    if (!isSLMExpanded || !Array.isArray(slm.children)) return [slmRow];

    const flmRows = slm.children.flatMap((flm, flmIndex) => {
      const flmRowKey = `FLM-${flm.Name}-${slmIndex}-${flmIndex}`;
      const isFLMSelected = selectedRowKey === flmRowKey;
const isFLMExpanded = expandedFLMPaths.includes(`${slmIndex}-${flmIndex}`);


      const flmRow = (
        <TableRow
          key={flmRowKey}
          sx={{
            cursor: "pointer",
            bgcolor: isFLMSelected ? "#454088" : "#ffffff",
            "&:hover": {
              bgcolor: isFLMSelected ? "#454088" : "#e0e0ff"
            },
            color: isFLMSelected ? "#ffffff" : undefined
          }}
          onClick={() => {
            handleFLMClick(slmIndex, flmIndex);
            setSelectedRowKey(flmRowKey);
          }}
        >
          {Object.keys(flm)
            .filter(k => k !== "children")
            .map(key => (
              <TableCell
                key={key}
                sx={{ color: isFLMSelected ? "#ffffff" : undefined, textAlign: "left" }}
              >
                {formatCellValue(key, flm[key])}
              </TableCell>
            ))}
        </TableRow>
      );

      const mrRows =
        isFLMExpanded && Array.isArray(flm.children)
          ? flm.children.map((mr, mrIndex) => {
              const mrRowKey = `MR-${mr.Name}-${slmIndex}-${flmIndex}-${mrIndex}`;
              return (
                <TableRow
                  key={mrRowKey}
                  sx={{
                    bgcolor: "#f9f9ff"
                  }}
                >
                  {Object.keys(mr)
                    .filter(k => k !== "children")
                    .map(key => (
                      <TableCell key={key} sx={{ textAlign: "left" }}>
                        {formatCellValue(key, mr[key])}
                      </TableCell>
                    ))}
                </TableRow>
              );
            })
          : [];

      return [flmRow, ...mrRows];
    });

    return [slmRow, ...flmRows];
  });
};


  const flattenRow = (row: TableRowData): TableRowData => {
    const { children, ...rest } = row;
    return rest;
  };

 const renderTable = () => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={2}>
        <CircularProgress />
      </Box>
    );
  }

  // Use fallback row structure for headers
  const flatFirstRow =
    tableData.length > 0 ? flattenRow(tableData[0]) : flattenRow(getFallbackData()[0]);

  return (
    <Box className="Customer-container" alignItems="center" mb={2}>
    <Typography className="Customer-title" variant="h6" textAlign="center" flex={1}>
  Parameter - ({activeCard === "Flm_Coverage" ? "FLM Coverage" : activeCard})
</Typography>

      <Typography variant="body2" className="Customer-hint" ml={2}>
        Click on the Name for Drill Down
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead className="Customer-table-head">
            <TableRow sx={{ padding: "6px , 6px" }}>
              {Object.keys(flatFirstRow)
                .filter((k) => k !== "children")
                .map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      fontWeight: "bold",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      width: "100px",
                      textAlign: "left",
                      lineHeight: 1.2,
                      padding: "4px"
                    }}
                  >
                    {(keyMapping[col] || col.replace(/_/g, " ")).replace(/Per$/, "%")}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.length > 0 ? (
              renderRows(tableData)
            ) : (
              <TableRow>
                <TableCell
                  colSpan={Object.keys(flatFirstRow).length}
                  align="center"
                  sx={{ fontStyle: "italic", color: "gray", textAlign: "center" }}
                >
                  No data available for this selection.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};


  return <Box>{renderTable()}</Box>;
});

export default CustomerCoverageTable;
